<template>
  <section class="analytics-section" v-if="canLoadWidgets" :key="'finance-analytics'">
    <section class="mb-3">
      <div>
        <b-row class="align-items-center">
          <b-col cols="12" md="auto" class="mb-2 mb-lg-0">
            <h3>Analytics - Parcels Shipped Stats</h3>
            <p class="text-muted mb-0">
              Analyze parcel shipment trends over time, grouped by different factors
            </p>
          </b-col>
          <b-col cols="12" md="5" class="mb-2 mb-lg-0">
            <ActiveFiltersCard
              :items-filters="filters"
              :load-items="applyFilters"
              :reset-filter-with-key="resetFilterWithKey"
            />
          </b-col>
          <b-col cols="12" md="2" class="text-lg-right ml-auto">
            <b-button
              variant="primary"
              @click="showFilterAside = true"
            >
              <i class="fa fa-filter"></i> Filters
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Filters Aside -->
      <b-sidebar
        v-model="showFilterAside"
        title="Filters"
        right
        shadow
        backdrop
      >
        <div class="px-3 py-2">
          <b-form-group label="For Client:" label-for="filter-brand-id">
            <b-v-select
              id="filter-brand-id"
              v-model="filterForm.brandId"
              :searchable="true"
              :options="brandsFilter"
            />
          </b-form-group>

          <b-form-group label="From Warehouse:" label-for="filter-warehouse-id" class="mt-3">
            <b-v-select
              id="filter-warehouse-id"
              v-model="filterForm.warehouseId"
              :searchable="true"
              :options="warehousesFilter"
            />
          </b-form-group>

          <b-form-group label="Carrier Code:" label-for="filter-carrier-code" class="mt-3">
            <b-form-input
              id="filter-carrier-code"
              v-model="filterForm.carrierCode"
              placeholder="Enter carrier code"
            />
          </b-form-group>

          <b-form-group label="Service Code:" label-for="filter-service-code" class="mt-3">
            <b-form-input
              id="filter-service-code"
              v-model="filterForm.serviceCode"
              placeholder="Enter service code"
            />
          </b-form-group>

          <b-form-group label="From Date" label-for="filter-from-date" class="mt-3">
            <b-form-datepicker
              id="filter-from-date"
              v-model="filterForm.fromDate"
              :max="filterForm.toDate"
            ></b-form-datepicker>
          </b-form-group>

          <b-form-group label="To Date" label-for="filter-to-date" class="mt-3">
            <b-form-datepicker
              id="filter-to-date"
              v-model="filterForm.toDate"
              :min="filterForm.fromDate"
            ></b-form-datepicker>
          </b-form-group>

          <div class="mt-4">
            <b-button
              variant="primary"
              block
              @click="applyFilters"
            >
              Apply Filters
            </b-button>
          </div>
        </div>
      </b-sidebar>

      <div class="animated fadeIn pt-3">
        <b-tabs content-class="mt-0 p-0">
          <b-tab title="By Carrier" active>
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="carrier"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Service">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="service"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Warehouse">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="warehouse"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Storefront">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="storefront"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Client">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="client"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Dimensions" v-if="is('developer')">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="dimensions"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="By Weight" v-if="is('developer')">
            <b-row>
              <b-col sm="12">
                <ChartPackageAnalytics
                  default-group-by="weight"
                  :brand-id="filters.brandId"
                  :warehouse-id="filters.warehouseId"
                  :carrier-code="filters.carrierCode"
                  :service-code="filters.serviceCode"
                  :show-widget-filters="false"
                  :filter-from-date="filters.fromDate"
                  :filter-to-date="filters.toDate"
                ></ChartPackageAnalytics>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </div>
    </section>
  </section>
</template>

<script>
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import ChartPackageAnalytics from '@/views/components/partials/analytics/ChartPackageAnalytics.vue';
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';

export default {
  name: 'FinanceAnalytics',
  components: {
    ChartPackageAnalytics,
    ActiveFiltersCard
  },
  mixins: [myCommonDataMixin],
  data: function() {
    var todaysDate = new Date();
    var startFromWhen = new Date();
    startFromWhen.setMonth(todaysDate.getMonth() - 3);

    return {
      canLoadWidgets: false,
      showFilterAside: false,
      filterForm: {
        brandId: '',
        warehouseId: '',
        carrierCode: '',
        serviceCode: '',
        fromDate: startFromWhen.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      },
      filters: {
        brandId: '',
        warehouseId: '',
        carrierCode: '',
        serviceCode: '',
        fromDate: startFromWhen.toISOString().split('T')[0],
        toDate: todaysDate.toISOString().split('T')[0]
      }
    };
  },
  mounted() {
    this.canLoadWidgets = true;
  },
  methods: {
    applyFilters: function() {
      this.filters = Object.assign({}, this.filterForm);
      this.showFilterAside = false;
    },
    resetFilterWithKey: function(key) {
      this.filterForm[key] = '';
      this.filters[key] = '';
    }
  }
};
</script>
